import React, { useState, useEffect } from 'react';
import { BRAND_NAME } from '../../theme/brand';
import { BsEye } from 'react-icons/bs';
import { FiEyeOff, FiMessageSquare, FiPhone } from 'react-icons/fi';
import * as Yup from 'yup';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWrapper } from '../../helpers';
import './login.css';
import { LOGO_SRC as logo } from "../../theme/brand";
import { CiLocationArrow1 } from "react-icons/ci";
import { FaRegBell } from 'react-icons/fa';
import { AiOutlineMobile } from "react-icons/ai";
import { MdOutlineEmail } from 'react-icons/md';
import { LuLock, LuMail } from 'react-icons/lu';
import { BiGitBranch } from 'react-icons/bi';
import { HiOutlineLightningBolt } from 'react-icons/hi';
import { HiOutlineUserGroup } from 'react-icons/hi';
import { MFAVerificationModal } from './MFAVarificationModal';
import { clearPermissionCache } from '../../hooks/usePagePermissions';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [mfaToken, setMfaToken] = useState('');

  const FormSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  });

  const domain_check = async () => {
    try {
      let domain_name = window.location.hostname.split(".")[0];

      if (process.env.REACT_APP_MODE === 'local') {
        domain_name = 'demo';
      }

      const verifyUrl = `${process.env.REACT_APP_API_URL}/customer-portal/check-domain`;
      const verifyResponse = await fetchWrapper.post(verifyUrl, { domain_name }).catch(e => {
        navigate("/domain-error");
      });
      localStorage.setItem("login_token", verifyResponse.login_token);
    } catch (err) {
      toast(`${err}`, {
        type: "error",
      });
    }
  };

  useEffect(() => {
    domain_check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMFASuccess = () => {
    clearPermissionCache();
    navigate("/dashboard");
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setErrors({});

      await FormSchema.validate(formData, { abortEarly: false });

      let data = formData;
      const url = `${process.env.REACT_APP_API_URL}/customer-portal/login`;
      const loginData = await fetchWrapper.post(url, data);

      if (loginData.mfa_required) {
        setMfaToken(loginData.mfa_token);
        setShowMFAModal(true);
      } else {
        const token = loginData.accessToken;
        localStorage.setItem("customer-portal", token);
        // Bug-006c: persist the per-user identity the login returns. Board/metric-tree
        // ownership checks read `user.id` from here; without it currentUserId was ''
        // and every owner-gated affordance (the board actions menu) silently vanished.
        localStorage.setItem("user", JSON.stringify({ id: loginData.id, username: loginData.username }));
        localStorage.setItem("mfa_enabled", loginData.mfa_enabled);
        localStorage.setItem("mfa_verified", loginData.mfa_verified);
        localStorage.setItem("twofa_required", loginData.twofa_required);
        clearPermissionCache();
        toast("Login successful!", {
          type: "success",
        });
        navigate("/dashboard");
      }
    } catch (err) {
      if (err.name === 'ValidationError') {
        const validationErrors = {};
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      } else {
        toast(`Login failed: ${err.message || err}`, {
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const channels = [
    {
      name: 'WhatsApp',
      icon: FiMessageSquare,
      iconClass: 'login-channel-icon-whatsapp',
      description: 'Rich messaging with media support'
    },
    {
      name: 'SMS',
      icon: CiLocationArrow1,
      iconClass: 'login-channel-icon-sms',
      description: 'Universal text messaging'
    },
    {
      name: 'Voice',
      icon: FiPhone,
      iconClass: 'login-channel-icon-voice',
      description: 'Automated voice calls'
    },
    {
      name: 'Push',
      icon: FaRegBell,
      iconClass: 'login-channel-icon-push',
      description: 'Mobile push notifications'
    },
    {
      name: 'Inbox',
      icon: AiOutlineMobile,
      iconClass: 'login-channel-icon-inapp',
      description: 'Inbox notifications'
    },
    {
      name: 'Email',
      icon: MdOutlineEmail,
      iconClass: 'login-channel-icon-email',
      description: 'Rich email notifications'
    }
  ];

  const marketingFeatures = [
    {
      name: 'Segments',
      icon: HiOutlineUserGroup,
      iconClass: 'login-channel-icon-voice',
      description: 'Dynamic audience segments powered by behavior and events'
    },
    {
      name: 'Event Tracking',
      icon: HiOutlineLightningBolt,
      iconClass: 'login-channel-icon-whatsapp',
      description: 'Capture real-time user actions and transactional events at scale'
    },
    {
      name: 'xFlow Automation',
      icon: BiGitBranch,
      iconClass: 'login-channel-icon-sms',
      description: 'Event-based journeys and multi-step communication flows'
    },


  ];

  return (
    <div className="login-container">
      <MFAVerificationModal
        isOpen={showMFAModal}
        onClose={() => setShowMFAModal(false)}
        mfaToken={mfaToken}
        onVerificationSuccess={handleMFASuccess}
      />

      {/* Left Side - Login Form */}
      <div className="login-left-side left-side-responsive">
        <div className="login-form-container">
          {/* Logo */}
          <div className="login-logo-section">
            <img src={logo} alt="Company Logo" />
            <h2 className="login-welcome-title">Welcome back</h2>
            <p className="login-welcome-subtitle">Sign in to your {BRAND_NAME} account</p>
          </div>

          {/* Form */}
          <form className="login-form-section" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="login-field-container">
              <label htmlFor="email" className="login-label">
                Email address
              </label>
              <div className="login-input-container">
                <div className="login-input-icon">
                  <LuMail />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className={`login-input ${errors.email ? 'login-input-error' : ''}`}
                  placeholder="Enter your Email"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="login-error-text">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="login-field-container">
              <label htmlFor="password" className="login-label">
                Password
              </label>
              <div className="login-input-container">
                <div className="login-input-icon">
                  <LuLock />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className={`login-input login-password-input ${errors.password ? 'login-input-error' : ''}`}
                  placeholder="Enter your Password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="login-eye-button"
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <BsEye />}
                </button>
              </div>
              {errors.password && (
                <p className="login-error-text">{errors.password}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="login-submit-button"
              aria-label="Login to your account"
            >
              {isLoading ? (
                <div className="login-spinner"></div>
              ) : (
                <>
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Right Side - Communication Channels */}
      <div className="login-right-side right-side-responsive">
        <div className="login-right-content" style={{ padding: '20px 0' }}>
          {/* Header */}
          <div className="login-feature-header" >
            <h3 className="login-feature-title" style={{ fontSize: '28px', marginBottom: '12px' }}>Enterprise Communication Platform</h3>
            <p className="login-feature-subtitle" style={{ fontSize: '14px', marginBottom: '0' }}>
              Multi-channel messaging with intelligent automation, real-time event tracking, and powerful audience segmentation.
            </p>
          </div>

          {/* Multi-Channel Notifications */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              gap: '16px'
            }}>
              <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #d1d5db, transparent)' }}></div>
              <h4 style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#6b7280',
                fontWeight: '500',
                margin: 0
              }}>
                Multi-Channel Delivery
              </h4>
              <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #d1d5db, transparent)' }}></div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {channels.map((channel) => {
                const IconComponent = channel.icon;
                return (
                  <div key={channel.name} className="login-channel-card" style={{ padding: '12px' }}>
                    <div className={`login-channel-icon ${channel.iconClass}`} style={{ width: '36px', height: '36px', marginBottom: '8px' }}>
                      <IconComponent style={{ fontSize: '18px' }} />
                    </div>
                    <h5 className="login-channel-name" style={{ fontSize: '14px', marginBottom: '4px' }}>{channel.name}</h5>
                    <p className="login-channel-description" style={{ fontSize: '12px' }}>{channel.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Marketing & Intelligence */}
          <div style={{ marginBottom: '24px', width: '100%' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              gap: '16px'
            }}>
              <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #d1d5db, transparent)' }}></div>
              <h4 style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#6b7280',
                fontWeight: '500',
                margin: 0
              }}>
                Marketing & Intelligence
              </h4>
              <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #d1d5db, transparent)' }}></div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {marketingFeatures.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <div key={feature.name} className="login-channel-card" style={{ padding: '12px' }}>
                    <div className={`login-channel-icon ${feature.iconClass}`} style={{ width: '36px', height: '36px', marginBottom: '8px' }}>
                      <IconComponent style={{ fontSize: '18px' }} />
                    </div>
                    <h5 className="login-channel-name" style={{ fontSize: '14px', marginBottom: '4px' }}>{feature.name}</h5>
                    <p className="login-channel-description" style={{ fontSize: '12px' }}>{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Features Footer */}
          <div className="login-features-footer" style={{ marginTop: '20px' }}>
            <div className="login-feature-item" style={{ fontSize: '12px' }}>
              <div className="login-feature-dot login-feature-dot-blue"></div>
              <span>Event-Driven Marketing</span>
            </div>
            <div className="login-feature-item" style={{ fontSize: '12px' }}>
              <div className="login-feature-dot login-feature-dot-green"></div>
              <span>Smart Routing</span>
            </div>
            <div className="login-feature-item" style={{ fontSize: '12px' }}>
              <div className="login-feature-dot login-feature-dot-blue"></div>
              <span>Real-Time Analytics</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;